import { useQuery } from '@apollo/client'
import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Module } from '@/modules/companies/consts'
import { ALL_TIME_ENTRY_PROJECTS_QUERY } from '@/modules/timesheets/queries'
import type {
  AllTimeEntryProjectsQuery,
  TimeEntryProjectNode,
} from '@/modules/timesheets/types.graphql'
import React from 'react'
import { isMobileOnly } from 'react-device-detect'
import styled from 'styled-components'
import type { RelayNodeType } from '@/types/graphql'
import { useConfirm, useModuleConfig } from '@/utils/hooks'
import type { TimeEntryProjectForm } from '../components'
import {
  InfoBox,
  ModuleOptionCards,
  OldSection,
  SectionHeader,
  SettingsWrapper,
  TimeEntryProjectPrompt,
} from '../components'
import { OldAddButton } from '../components/common'
import { TimeEntryProjectSettingsTable } from '../components/TimeEntrySettings/TimeEntryProjectSettingsTable'
import { useTimesheetSettingsMutations } from '../util'

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`

interface TimeEntrySettingsProps {}

export const TimeEntrySettings: React.FC<TimeEntrySettingsProps> = () => {
  const translations = useTranslate({
    info1: 'settings.info.time-entries-1',

    moduleOptions: 'settings.module-options',
    timeEntryProjects: 'settings.time-entry-projects',

    prompt: {
      delete: ['settings.prompts.delete-time-entry-project', { name: '' }],
      deleteTitle: 'settings.prompts.time-entry-project-delete-title',
    },
  })
  const addPrompt = usePrompt()
  const confirm = useConfirm()

  const { moduleConfig } = useModuleConfig(Module.TIMESHEETS)

  const { data } = useQuery<AllTimeEntryProjectsQuery, {}>(
    ALL_TIME_ENTRY_PROJECTS_QUERY
  )

  const projects =
    data?.allTimeEntryProjects?.edges?.map(edge => edge.node) ?? []

  const mutations = useTimesheetSettingsMutations()

  async function handleAddTimeEntryProject() {
    const { data } = await addPrompt<TimeEntryProjectForm | null>(resolve => (
      <TimeEntryProjectPrompt
        existingTimeEntryProjects={projects}
        onSubmit={resolve}
      />
    ))
    if (data === null) return

    void mutations.create({
      variables: {
        input: { name: data.name },
      },
    })
  }

  async function handleEditTimeEntryProject(project: TimeEntryProjectNode) {
    const { data } = await addPrompt<TimeEntryProjectForm | null>(resolve => (
      <TimeEntryProjectPrompt
        initialData={project}
        existingTimeEntryProjects={projects}
        onSubmit={resolve}
      />
    ))
    if (data === null) return

    void mutations.patch({
      variables: {
        id: project.id,
        input: { name: data.name },
      },
    })
  }

  async function handleDeleteTimeEntryProject(
    project: RelayNodeType<AllTimeEntryProjectsQuery['allTimeEntryProjects']>
  ) {
    const { data: answer } = await confirm(
      translations.prompt.delete({ name: project.name }),
      translations.prompt.deleteTitle
    )
    if (!answer) return

    void mutations.delete({
      variables: {
        id: project.id,
      },
    })
  }

  return (
    <SettingsWrapper grid={{ flow: 'row' }}>
      <InfoBox initCollapsed={isMobileOnly}>
        <p>{translations.info1}</p>
      </InfoBox>

      <OldSection>
        <Header>
          <SectionHeader>{translations.moduleOptions}</SectionHeader>
        </Header>

        {!!moduleConfig && <ModuleOptionCards module={Module.TIMESHEETS} />}
      </OldSection>
      <OldSection>
        <Header>
          <SectionHeader>{translations.timeEntryProjects}</SectionHeader>
          <OldAddButton adding={false} onClick={handleAddTimeEntryProject} />
        </Header>
        {!!projects && (
          <TimeEntryProjectSettingsTable
            timeEntryProjects={projects}
            onEditSubmit={handleEditTimeEntryProject}
            onDelete={handleDeleteTimeEntryProject}
          />
        )}
      </OldSection>
    </SettingsWrapper>
  )
}
